import React, { useMemo } from "react";
import styled from "styled-components";
import { SkeletonLoader, colors2 } from "oolib";
import { UI_BODY_SM, UI_BODY_SEMIBOLD_SM_DF, UI_BODY_SEMIBOLD_DF } from "oolib/dist/v2/components/Typo2";
import { Heart } from "@phosphor-icons/react";
import { DashboardWidgetShell } from "../../comps/DashboardWidgetShell";
import { StatusPill } from "../comps/StatusPill";
import { sentimentPillTheme } from "../sentiment";
import { useLensesV2Context } from "../LensesV2Context";
import { useTagCooccurrence } from "@/components/analysisModules/AnnotationExplorer/comps/AnnotationExplorerVizPanel/queries/useTagCooccurrence";
import { useGetAnnotationsSummary } from "@/utils/react-query-hooks/annotationSummary";
import { buildTagScopedFindQuery, COOCCURRENCE_FULL } from "../utils";
import { featureSentimentSides, assembleFeatures } from "./utils";
import { CONFIG } from "./CONFIG";

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
`;

const FeatureRowWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding: 1.2rem 0;
  border-bottom: 1px solid ${colors2.grey5};

  &:last-child {
    border-bottom: none;
  }
`;

const FeatureRowTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;

const FeatureDescription = styled(UI_BODY_SM)`
  color: ${colors2.grey60};
  line-height: 1.5;
`;

// One feature row — name + sentiment/health pill + its own summary (v1 shows the
// summary inline by default, so each row fires the summary hook on mount; the BE
// caches, so repeat loads are free). Short, card-sized summary.
const FeatureRow = ({ feature, findQuery }) => {
  const summaryQuery = useMemo(
    () => buildTagScopedFindQuery(findQuery, [feature.featureId]),
    [findQuery, feature.featureId]
  );
  const summary = useGetAnnotationsSummary({
    scope: { findQuery: summaryQuery },
    outputLength: { words: 40, maxSentences: 2 },
    outputFormat: "plainText",
    enabled: !!summaryQuery,
  });

  const theme = sentimentPillTheme(feature.sentiment);
  const text = summary.data?.summary;

  return (
    <FeatureRowWrap>
      <FeatureRowTop>
        <UI_BODY_SEMIBOLD_DF style={{ color: colors2.grey90 }}>{feature.feature}</UI_BODY_SEMIBOLD_DF>
        <StatusPill theme={theme} />
      </FeatureRowTop>
      {summary.isLoading ? (
        <SkeletonLoader style={{ height: 10, width: "80%", borderRadius: 4, marginTop: 4 }} />
      ) : summary.isError ? (
        <FeatureDescription style={{ color: "#dc2626" }}>
          Summary failed: {summary.error?.response?.data?.error || summary.error?.message}
        </FeatureDescription>
      ) : text ? (
        <FeatureDescription>{text}</FeatureDescription>
      ) : summary.data?.empty ? (
        <FeatureDescription style={{ fontStyle: "italic", color: colors2.grey40 }}>
          No annotations to summarize.
        </FeatureDescription>
      ) : null}
    </FeatureRowWrap>
  );
};

// Feature widget — per feature: name, sentiment/health pill, and an inline summary.
// Same v1 FeatureHealthBlock list layout. Mention count + sentiment come from ONE
// features × (emotion/affect | signals) cooccurrence call; the summary is per-feature
// via the shared annotationSummarizer. No trend (v1 shows none for features).
export const FeatureWidget = () => {
  const { findQuery, features, signals, emotionAffect } = useLensesV2Context();

  const cooccurrence = useTagCooccurrence({
    sides: featureSentimentSides(CONFIG.sentimentSource),
    ...COOCCURRENCE_FULL, // full unfiltered set — no top-N slice
    findQuery,
    pinSideBAsTagB: true, // feature → tagA, emotion/signal → tagB
    enabled: !!findQuery,
  });

  const features_ = useMemo(
    () => assembleFeatures(features, cooccurrence.data, { signals, emotionAffect }, CONFIG.sentimentSource),
    [features, cooccurrence.data, signals, emotionAffect]
  );
  const rows = features_.slice(0, CONFIG.maxFeatures);

  return (
    <DashboardWidgetShell
      titleJSX={
        <TitleWrapper>
          <Heart size={16} color={colors2.grey80} weight="bold" />
          <UI_BODY_SEMIBOLD_SM_DF>Trending product features health</UI_BODY_SEMIBOLD_SM_DF>
        </TitleWrapper>
      }
      style={{ gap: 0 }}
      bodyStyle={{ overflowY: "auto", overflowX: "hidden", maxHeight: 560 }}
    >
      {cooccurrence.isLoading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} style={{ padding: "8px 0" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <SkeletonLoader style={{ height: 14, width: `${35 + i * 8}%`, borderRadius: 4 }} />
                <SkeletonLoader style={{ height: 22, width: 90, borderRadius: 12 }} />
              </div>
              <SkeletonLoader style={{ height: 10, width: "80%", borderRadius: 4, marginTop: 8 }} />
            </div>
          ))}
        </div>
      )}

      {cooccurrence.isError && (
        <UI_BODY_SM style={{ color: "#dc2626" }}>
          Error: {cooccurrence.error?.response?.data?.message || cooccurrence.error?.message}
        </UI_BODY_SM>
      )}

      {cooccurrence.data && rows.length === 0 && (
        <UI_BODY_SM style={{ color: colors2.grey40 }}>No feature data available yet.</UI_BODY_SM>
      )}

      {cooccurrence.data && rows.length > 0 && (
        <FeatureList>
          {rows.map((f) => (
            <FeatureRow key={f.featureId} feature={f} findQuery={findQuery} />
          ))}
        </FeatureList>
      )}
    </DashboardWidgetShell>
  );
};
